import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../../../app/core/services/notification.service';

/**
 * Token de contexto HTTP equivalente ao `meta.skipErrorHandling` do AngularJS.
 * Use quando o componente trata o erro manualmente e exibe sua própria mensagem.
 *
 * Equivalente AngularJS:
 *   $http.get(url, { meta: { skipErrorHandling: () => true } })
 *
 * Uso no Angular:
 *   this.http.post(url, body, { context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) })
 */
export const SKIP_ERROR_NOTIFICATION = new HttpContextToken<boolean>(() => false);

/**
 * Interceptor global de notificação de erros HTTP.
 * Port TypeScript do ed-error-interceptor.js do AngularJS legado.
 *
 * Comportamento original preservado:
 * - Ignora status 401, 403, 404 (e 0 = rede/CORS)
 * - Ignora URLs de infraestrutura (equivalente ao ignoreUrlPatterns do AngularJS)
 * - Se `detail` for array: exibe cada mensagem individualmente
 * - Se `detail` não existir: exibe `message` + ` - classificationCode` (se houver)
 * - Permite opt-out via SKIP_ERROR_NOTIFICATION (equivalente ao skipErrorHandling)
 * - Só exibe se houver `data` na resposta de erro
 */
@Injectable()
export class HttpErrorNotificationInterceptor implements HttpInterceptor {

  // Equivalente a: ignoredHttpErrorCodes = [401, 403, 404]
  // + status 0 (CORS/rede) tratado pelo AuthErrorInterceptorService
  private readonly IGNORED_STATUSES = [0, 401, 403, 404];

  // Equivalente ao ignoreUrlPatterns do http-error-handler.config.js do AngularJS
  // Erros nessas URLs são silenciados (sem toast) — os serviços tratam individualmente via try/catch
  private readonly IGNORED_URL_PATTERNS = [
    /my-preferences\/api/,
    /machine-learning/,
    /access\/undefined\/permissions/,
    /storage\/dados/,
  ];

  constructor(private notificationService: NotificationService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.context.get(SKIP_ERROR_NOTIFICATION)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const isIgnoredStatus = this.IGNORED_STATUSES.includes(error.status);
        const isIgnoredUrl = this.IGNORED_URL_PATTERNS.some(pattern => pattern.test(req.url));

        if (!isIgnoredStatus && !isIgnoredUrl && error.error) {
          this.notifyMessage(error);
        }
        return throwError(() => error);
      })
    );
  }

  /** Port do notifyMessage() do ed-error-interceptor.js: prioriza `detail`, senão `message` + classificationCode */
  private notifyMessage(error: HttpErrorResponse): void {
    const data = error.error;
    if (!data || typeof data !== 'object') {
      return;
    }

    const title = this.getTitleByStatus(error.status);

    const message = this.extractMessage(data);
    if (message) {
      this.notificationService.error({ title, message });
    }
  }

  private extractMessage(data: Record<string, unknown>): string {
    const detail = data['detail'];
    if (detail && this.isNotEmpty(detail)) {
      return this.extractDetailMessage(detail);
    }

    let message = (data['message'] as string) || (data['erro'] as string) || '';
    if (data['classificationCode'] && message) {
      message += `\nCódigo: ${data['classificationCode']}`;
    }
    return message;
  }

  private extractDetailMessage(detail: unknown): string {
    if (Array.isArray(detail)) {
      return detail.map((item) => this.extractDetailItem(item)).filter(Boolean).join('\n');
    }

    if (typeof detail === 'object' && detail !== null) {
      return Object.values(detail as Record<string, unknown>).filter(Boolean).join('\n');
    }

    return this.extractDetailItem(detail);
  }

  private extractDetailItem(item: unknown): string {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      return (obj['message'] || obj['msg'] || obj['erro'] || '') as string;
    }
    return '';
  }

  /** Equivalente a: !_.isEmpty(value) */
  private isNotEmpty(value: unknown): boolean {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.length > 0;
    if (value && typeof value === 'object') return Object.keys(value).length > 0;
    return Boolean(value);
  }

  private getTitleByStatus(status: number): string {
    if (status === 422) return 'Dados inválidos';
    if (status >= 500) return 'Erro no servidor';
    return 'Erro';
  }
}
